import { FC, SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { StyledTab, StyledTabs } from './styles';
import Tags from './Transcript/Tags';
// import VideoTranscript from './VideoPlayer/VideoTranscript';  // Hidden temporarily
// import AudioTranscript from './AudioPlayer/AudioTranscript';  // Hidden temporarily
import { CircularProgress, Stack, useTheme, Skeleton, Typography, Box, Button } from '@mui/material';
import { DocumentChapter, Transcript } from '@/types/note';
import { TranscriptList } from './Transcript/styles';
import FullTranscript from './Transcript/FullTranscript';
import UpgradePrompt from '@/components/UpgradePrompt';
import { useCurrentPlanFeatures } from '@/hooks/useCurrentPlanFeatures';
import LockIcon from '@mui/icons-material/Lock';
import { logger } from '@/services/loggerService';
import { parseTime } from '@/helpers/formatTime';
import { FormatsTabContent } from '@/features/noteFormats';
import { CommentsPanel } from '@/features/comments';
import SilentErrorBoundary from '@/components/SilentErrorBoundary';
import { useAuthStore } from '@/stores/authStore';
import { hasBetaFeature } from '@/utils/subscriptionHelpers';

interface RightTabsProps {
  tags: string[];
  isAudioOnly: boolean;
  isOwner: boolean;
  isLoading?: boolean;
  isProcessing?: boolean;
  isNoteProcessing?: boolean;
  fullTranscriptList?: Transcript[] | null;
  keyMoments?: DocumentChapter[] | null;
  refetchNote?: () => void;
  isPublicView?: boolean;
  noteId?: string;
  shareToken?: string | null;
  fullTranscriptUrl?: string | null;
  onKeyPointsUpdate?: (keyPoints: Transcript[]) => void;
}

const RightTabs: FC<RightTabsProps> = ({
  tags,
  isAudioOnly,
  isOwner,
  isLoading,
  isProcessing,
  isNoteProcessing,
  fullTranscriptList,
  keyMoments,
  refetchNote,
  isPublicView = false,
  noteId,
  shareToken,
  fullTranscriptUrl,
  onKeyPointsUpdate,
}) => {
  const { features, betaFeatures, isLoading: isFeaturesLoading } = useCurrentPlanFeatures();
  const userData = useAuthStore((s) => s.userData);
  const isAuthenticated = Boolean(userData?.id);
  const showFormats = !isPublicView && hasBetaFeature(userData, betaFeatures, 'noteFormats');
  // Temporarily hide the original Keypoints tab and rename Transcript to Key Points
  const tabs = isPublicView
    ? ['Transcript', 'Comments']
    : ['Transcript', 'Comments', ...(showFormats ? ['Formats'] : [])];
  const theme = useTheme();
  const [value, setValue] = useState(tabs[0]);
  const [searchParams] = useSearchParams();
  const didOpenCommentDeepLink = useRef(false);

  const handleChange = (_event: SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  // Email notifications deep-link to ?comment=<id> (new) or ?thread=<id> (legacy).
  // Open the Comments tab once so the target comment can scroll into view.
  useEffect(() => {
    if (didOpenCommentDeepLink.current) return;
    if (searchParams.get('comment') || searchParams.get('thread')) {
      setValue('Comments');
      didOpenCommentDeepLink.current = true;
    }
  }, [searchParams]);

  const isUsingRealTranscript = Boolean(fullTranscriptList && fullTranscriptList.length > 0);

  const effectiveKeyPoints = useMemo<Transcript[]>(() => {
    if (fullTranscriptList && fullTranscriptList.length > 0) {
      return fullTranscriptList;
    }

    if (!keyMoments || keyMoments.length === 0) {
      return [];
    }

    return keyMoments.map((moment, index) => ({
      timecode: parseTime(moment.timecode),
      title: null,
      description: moment.summary?.trim() || moment.text || `Key point ${index + 1}`,
      text: moment.summary?.trim() || moment.text || `Key point ${index + 1}`,
    }));
  }, [fullTranscriptList, keyMoments]);

  useEffect(() => {
    logger.debug('notes', 'RightTabs key points visibility state', {
      isLoading: Boolean(isLoading),
      isFeaturesLoading,
      aiKeyPointsEnabled: features?.aiKeyPoints ?? false,
      aiTaggingEnabled: features?.aiTagging ?? false,
      fullTranscriptCount: fullTranscriptList?.length ?? 0,
      keyMomentsCount: keyMoments?.length ?? 0,
      effectiveKeyPointsCount: effectiveKeyPoints.length,
      isProcessing: Boolean(isProcessing),
      isNoteProcessing: Boolean(isNoteProcessing),
    });
  }, [
    features?.aiKeyPoints,
    features?.aiTagging,
    keyMoments?.length,
    fullTranscriptList?.length,
    effectiveKeyPoints.length,
    isFeaturesLoading,
    isLoading,
    isNoteProcessing,
    isProcessing,
  ]);

  const renderFullTranscriptSkeleton = () => (
    <Stack spacing={2}>
      <TranscriptList>
        {[...Array(4)].map((_, index) => (
          <Stack
            key={index}
            bgcolor={'common.white'}
            borderRadius={3}
            height={100}
            p={2}
            flexDirection={'row'}
            gap={1}
            mb={3}
          >
            <Skeleton variant="rounded" width="15%" height={30} />
            <Skeleton variant="rounded" width="85%" height={60} />
          </Stack>
        ))}
      </TranscriptList>
    </Stack>
  );

  const views: { [key: string]: JSX.Element | null } = {
    Transcript:
      isLoading || isFeaturesLoading ? (
        // Show skeleton while loading note data or determining user's plan features
        renderFullTranscriptSkeleton()
      ) : (
        <>
          {/* Tags section - shown above key points */}
          {!isNoteProcessing &&
            (isPublicView || features.aiTagging ? (
              <Tags initialTags={tags} isOwner={isOwner} onTagsUpdated={refetchNote} />
            ) : tags.length > 0 ? (
              // A note's existing tags are never gated behind an upgrade wall. Public
              // viewers already see them, so the owner must too — regardless of note
              // age (CONTRACT: notes open normally regardless of age). Rendered
              // read-only via isOwner={false}; creating/managing tags stays plan-gated.
              <Tags initialTags={tags} isOwner={false} onTagsUpdated={refetchNote} />
            ) : (
              <UpgradePrompt featureName="AI tagging" description="AI tagging is an upgraded feature" sx={{ mb: 1 }} />
            ))}
          {/* Key Points section - existing key points are never gated behind an upgrade
              wall (CONTRACT #11); only the empty state is plan-gated */}
          {effectiveKeyPoints.length > 0 ? (
            <FullTranscript
              transcriptList={effectiveKeyPoints}
              isAudioOnly={isAudioOnly}
              isOwner={(isPublicView || features.aiKeyPoints) && isOwner}
              isProcessing={isNoteProcessing}
              fullTranscriptUrl={isUsingRealTranscript ? fullTranscriptUrl : null}
              onKeyPointsUpdate={onKeyPointsUpdate}
            />
          ) : isPublicView || features.aiKeyPoints ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
              No key points available yet.
            </Typography>
          ) : (
            <UpgradePrompt featureName="AI Key Points" description="AI-generated key points are an upgraded feature" />
          )}
        </>
      ),
    Comments: isFeaturesLoading ? (
      // Show loading spinner while determining user's plan features
      <Stack justifyContent={'center'} alignItems={'center'} sx={{ minHeight: '200px' }}>
        <CircularProgress />
      </Stack>
    ) : !isAuthenticated ? (
      // Commenting requires a logged-in user
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '200px',
          p: 3,
          textAlign: 'center',
          bgcolor: 'background.paper',
          borderRadius: 2,
        }}
      >
        <LockIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.primary" gutterBottom>
          Sign in to comment
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Create an account or sign in to view and leave comments on this note.
        </Typography>
        <Button variant="contained" color="primary" href="/login?action=signup" size="medium">
          Sign In
        </Button>
      </Box>
    ) : features.commenting && noteId ? (
      <SilentErrorBoundary feature="notes" operation="comments">
        <CommentsPanel noteId={noteId} shareToken={shareToken} isOwner={isOwner} />
      </SilentErrorBoundary>
    ) : (
      <UpgradePrompt featureName="Comments" description="Comments are an upgraded feature." />
    ),
    Formats: noteId ? <FormatsTabContent noteId={noteId} /> : null,
  };

  return (
    <Stack
      p={2}
      bgcolor={theme.palette.grey[50]}
      borderRadius={3}
      sx={{
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        [theme.breakpoints.down('sm')]: {
          padding: 1,
          borderRadius: 2,
        },
      }}
    >
      <StyledTabs variant="fullWidth" value={value} onChange={handleChange}>
        {tabs.map((tab) => (
          <StyledTab key={tab} value={tab} label={tab === 'Transcript' ? 'Key Points' : tab} disableRipple />
        ))}
      </StyledTabs>
      <div
        style={{
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        {views[value]}
      </div>
    </Stack>
  );
};

export default RightTabs;
